import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { LightbulbIcon, ShoppingCartIcon, ChartLineUpIcon, ArrowsCounterClockwiseIcon, CheckSquareIcon, SquareIcon, StarIcon } from "@phosphor-icons/react";

export default function AutoOC() {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [overrides, setOverrides] = useState({});           // {producto_id: {cantidad, precio, proveedor_id}}
  const [formaPago, setFormaPago] = useState("credito");
  const [diasCredito, setDiasCredito] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState("sugerencias");
  const [status, setStatus] = useState([]);
  const [soloBajo, setSoloBajo] = useState(true);

  const loadStatus = async (sb = soloBajo) => {
    try { const { data } = await api.get("/inventory/stock-status", { params: { solo_bajo_minimo: sb } }); setStatus(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, sup] = await Promise.all([api.get("/purchases/auto-suggest"), api.get("/suppliers")]);
      setSuggestions(s.data); setSuppliers(sup.data);
      // Pre-seleccionar las que tienen proveedor sugerido
      const pre = new Set(s.data.filter((x) => x.proveedor_id).map((x) => x.producto_id));
      setSelected(pre);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadStatus(); }, []);

  const toggle = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const toggleAll = () => setSelected(new Set(selected.size === suggestions.length ? [] : suggestions.map((s) => s.producto_id)));

  const getVal = (s, k) => overrides[s.producto_id]?.[k] ?? (k === "cantidad" ? s.cantidad_sugerida : k === "precio" ? s.ultimo_costo : s.proveedor_id);
  const setVal = (id, k, v) => setOverrides({ ...overrides, [id]: { ...(overrides[id] || {}), [k]: v } });

  // Agrupado por proveedor para mostrar el resumen
  const grouped = {};
  for (const s of suggestions.filter((x) => selected.has(x.producto_id))) {
    const sup = getVal(s, "proveedor");
    if (!sup) continue;
    const cant = Number(getVal(s, "cantidad")) || 0;
    const precio = Number(getVal(s, "precio")) || 0;
    grouped[sup] = grouped[sup] || { proveedor_nombre: suppliers.find((x) => x.id === sup)?.nombre || "—", items: 0, total: 0 };
    grouped[sup].items += 1;
    grouped[sup].total += cant * precio;
  }

  const generate = async () => {
    const items = suggestions.filter((s) => selected.has(s.producto_id)).map((s) => ({
      producto_id: s.producto_id,
      cantidad: Number(getVal(s, "cantidad")),
      precio_unitario: Number(getVal(s, "precio")),
      proveedor_id: getVal(s, "proveedor") || "",
    }));
    const missing = items.filter((i) => !i.proveedor_id);
    if (missing.length) { toast.error(`${missing.length} líneas sin proveedor asignado`); return; }
    if (!items.length) { toast.error("Selecciona al menos 1 producto"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/purchases/auto-generate", { items, forma_pago: formaPago, dias_credito: Number(diasCredito) });
      toast.success(`${data.total_ocs} OC(s) creadas en borrador`);
      setTimeout(() => navigate("/compras"), 1500);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setGenerating(false); }
  };

  return (
    <Layout title="Auto-OC · Reorden inteligente" subtitle={`${suggestions.length} productos bajo mínimo · Genera órdenes de compra agrupadas por proveedor con un clic`}
      actions={
        <button onClick={load} className="px-3 py-1.5 text-xs border border-border text-text-secondary rounded-md flex items-center gap-1.5" data-testid="reload-suggestions">
          <ArrowsCounterClockwiseIcon size={14} /> Refrescar
        </button>
      }
    >
      <div className="border-b border-border mb-4 flex gap-1" data-testid="autooc-tabs">
        {[{ id: "sugerencias", l: "Sugerencias de reorden" }, { id: "estado", l: "Estado de existencias" }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`autooc-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm border-b-2 ${tab === t.id ? "border-agro-green text-agro-green font-medium" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === "estado" ? (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="stock-status">
          <div className="p-3 border-b border-border/60 flex items-center justify-between">
            <div className="text-xs text-text-muted">Cantidad a reordenar = máximo − existencia (o mínimo − existencia si no hay máximo), cuando la existencia llega al mínimo.</div>
            <label className="text-xs flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={soloBajo} className="accent-agro-green"
                onChange={(e) => { setSoloBajo(e.target.checked); loadStatus(e.target.checked); }} /> Solo bajo mínimo
            </label>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left"><th className="p-3 pl-5">Producto</th><th className="p-3 text-right">Existencia</th><th className="p-3 text-right">Mínimo</th><th className="p-3 text-right">Máximo</th><th className="p-3 text-right">A reordenar</th><th className="p-3 pr-5">Proveedor</th></tr>
            </thead>
            <tbody>
              {status.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-text-muted">Sin productos</td></tr> : status.map((r) => (
                <tr key={r.producto_id} className="border-t border-border/40">
                  <td className="p-3 pl-5 text-xs"><span className="font-mono text-agro-green">{r.codigo}</span> · {r.nombre}</td>
                  <td className={`p-3 text-right ${r.estado === "bajo_minimo" ? "text-red-400" : ""}`}>{r.existencia} {r.unidad}</td>
                  <td className="p-3 text-right">{r.stock_minimo}</td>
                  <td className="p-3 text-right">{r.stock_maximo || "—"}</td>
                  <td className="p-3 text-right font-semibold text-agro-yellow">{r.cantidad_reordenar || "—"}</td>
                  <td className="p-3 pr-5 text-xs text-text-secondary">{r.proveedor_principal || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : loading ? (
        <div className="text-center text-text-muted py-12">Calculando productos bajo mínimo…</div>
      ) : suggestions.length === 0 ? (
        <div className="bg-card border border-border/60 rounded-lg p-12 text-center">
          <LightbulbIcon size={48} className="text-agro-green mx-auto mb-3 opacity-50" />
          <h3 className="font-heading text-lg text-text-primary mb-2">Todo en orden</h3>
          <p className="text-sm text-text-muted">Ningún producto está por debajo de su stock mínimo. Vuelve cuando la existencia baje.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Resumen ejecutivo por proveedor */}
          <div className="bg-card border border-border/60 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-heading text-sm uppercase tracking-wider text-agro-green flex items-center gap-2"><ChartLineUpIcon size={16} /> Resumen de la generación</h4>
              <div className="flex gap-2 items-center">
                <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)} className="bg-app border border-border rounded px-2 py-1 text-xs" data-testid="forma-pago">
                  <option value="contado">Contado</option><option value="credito">Crédito</option>
                </select>
                {formaPago === "credito" && (
                  <input type="number" value={diasCredito} onChange={(e) => setDiasCredito(e.target.value)} placeholder="Días" className="bg-app border border-border rounded px-2 py-1 text-xs w-20" data-testid="dias-credito" />
                )}
              </div>
            </div>
            {Object.keys(grouped).length === 0 ? (
              <div className="text-xs text-text-muted">Selecciona productos para ver el resumen.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {Object.entries(grouped).map(([sid, g]) => (
                  <div key={sid} className="bg-app border border-border/40 rounded p-2 text-sm">
                    <div className="text-text-primary font-medium truncate">{g.proveedor_nombre}</div>
                    <div className="text-[10px] text-text-muted">{g.items} líneas</div>
                    <div className="text-agro-green font-semibold">{fmtL(g.total)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tabla editable */}
          <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-sidebar">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-3 pl-5 w-10">
                    <button onClick={toggleAll} className="text-agro-green">{selected.size === suggestions.length ? <CheckSquareIcon size={16} weight="fill" /> : <SquareIcon size={16} />}</button>
                  </th>
                  <th className="p-3">Producto</th>
                  <th className="p-3 text-right">Existencia / Mín / Máx</th>
                  <th className="p-3 text-right">Cant. sugerida</th>
                  <th className="p-3 text-right">Costo unit.</th>
                  <th className="p-3">Proveedor</th>
                  <th className="p-3 text-right pr-5">Valor estim.</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s) => {
                  const sel = selected.has(s.producto_id);
                  const cant = Number(getVal(s, "cantidad")) || 0;
                  const precio = Number(getVal(s, "precio")) || 0;
                  const proveedor_id = getVal(s, "proveedor") || "";
                  const valor = cant * precio;
                  return (
                    <tr key={s.producto_id} className={`border-t border-border/40 ${sel ? "bg-agro-green/5" : ""}`} data-testid={`auto-row-${s.producto_codigo}`}>
                      <td className="p-3 pl-5"><input type="checkbox" checked={sel} onChange={() => toggle(s.producto_id)} className="accent-agro-green" /></td>
                      <td className="p-3 text-xs"><div className="font-mono text-agro-green">{s.producto_codigo}</div><div className="text-text-secondary truncate max-w-[200px]">{s.producto_nombre}</div></td>
                      <td className="p-3 text-right text-xs"><span className="text-red-400">{s.existencia}</span> / {s.min_stock} / {s.max_stock || "—"}
                        {(s.detalle_bodegas || []).map((b) => <div key={b.bodega_id} className="text-[10px] text-text-muted">{b.bodega_codigo}: {b.existencia}/{b.min_stock} → +{b.cantidad_sugerida}</div>)}</td>
                      <td className="p-3 text-right"><input type="number" value={getVal(s, "cantidad")} onChange={(e) => setVal(s.producto_id, "cantidad", e.target.value)} className="w-24 bg-app border border-border rounded px-2 py-1 text-xs text-right" data-testid={`qty-${s.producto_codigo}`} /></td>
                      <td className="p-3 text-right"><input type="number" step="0.01" value={getVal(s, "precio")} onChange={(e) => setVal(s.producto_id, "precio", e.target.value)} className="w-24 bg-app border border-border rounded px-2 py-1 text-xs text-right" data-testid={`price-${s.producto_codigo}`} /></td>
                      <td className="p-3">
                        <select value={proveedor_id} onChange={(e) => setVal(s.producto_id, "proveedor", e.target.value)} className="w-full bg-app border border-border rounded px-2 py-1 text-xs" data-testid={`sup-${s.producto_codigo}`}>
                          <option value="">— Asignar —</option>
                          {suppliers.map((sup) => <option key={sup.id} value={sup.id}>{sup.codigo} · {sup.nombre}</option>)}
                        </select>
                        {s.proveedor_origen && getVal(s, "proveedor") === s.proveedor_id && (
                          <div className="text-[10px] text-text-muted mt-0.5">{s.proveedor_origen === "preferido" ? <><StarIcon size={11} weight="fill" className="inline align-[-2px]" aria-hidden="true" /> Proveedor preferido del producto</> : "Última OC"}</div>
                        )}
                      </td>
                      <td className="p-3 pr-5 text-right text-sm">{fmtL(valor)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button disabled={generating || selected.size === 0} onClick={generate} data-testid="generate-btn"
              className="px-6 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover flex items-center gap-2 disabled:opacity-40">
              <ShoppingCartIcon size={16} weight="bold" /> {generating ? "Generando…" : `Generar ${Object.keys(grouped).length} OC(s) con ${selected.size} línea(s)`}
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
